<template>
  <v-card class="timer-card disabled"
          :class="[ borderColor, {'unSelectedByDoctor': isDisabled} ]"
          @click="$emit('click')">
    <span class="text-subtitle-2 treatment-count">
      {{ numberCompleted }} | {{ numberOfTreatments }}
    </span>
    <span
      class="text-caption treatment-time text--secondary"
      v-text="$filters.timestampString(timeInTreatment)"
    ></span>
    <span
      class="text-h5 appointment-time"
      :class="timeTextColor"
      v-text="$filters.timestampString(timeElapsed)"
    ></span>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapState } from 'vuex';

export default defineComponent({
  name: 'TimerCard',
  props: {
    appointment: { type: Object, required: true },
    appointmentState: { type: String, required: true },
    numberOfTreatments: { type: Number, required: true },
    roomId: { type: String, required: true },
    isDisabled: {type: Boolean, default: false},
  },
  data() { return { treatmentState: 'inactive' };},
  computed: {
    ...mapState({
      timeNow: ({ time: x }) => x,
      serverClockOffset: ({ auth }) => auth.serverClockOffset ?? 0,
    }),
    isOverTime() { return this.timeElapsed > this.timeAllocated; },
    numberCompleted() {
      return this.appointment.treatments.filter((t) => !!t.ended_at).length;
    },
    isOverTimeAlmost() {
      return this.timeElapsed > (this.timeAllocated - 180000);
    },
    timeAllocated(): number {
      return this.appointment.treatments?.reduce(
        (acc: number, { duration: x }) => acc + (x * 1000),
        0,
      ) ?? 0;
    },
    serverAdjustedNow(): number {
      return this.timeNow - this.serverClockOffset;
    },
    timeElapsed(): number {
      const [startedAt = this.serverAdjustedNow] = this.appointment.treatments
        .filter(({ started_at: x }) => !!x)
        .map(({ started_at: x }) => this.$dayjs.utc(x).valueOf())
        .sort();
      return Math.max(0, this.serverAdjustedNow - startedAt);
    },
    timeInTreatment(): number {
      return this.appointment.treatments
        .map(({
          ended_at: timeEnded,
          resumed_at: timeResumedAt,
          started_at: timeStarted,
          status,
          time_elapsed: timeElapsedBeforeSeconds,
        }) => {
          const isPaused = status === 'paused';
          const timeElapsedBefore = timeElapsedBeforeSeconds * 1000;
          const timeResumed =
            (!!timeStarted && !isPaused && !!timeResumedAt)
              ? this.$dayjs.utc(timeResumedAt).valueOf()
              : 0;
          const timeSinceResume =
            (!!timeStarted && !timeEnded && !isPaused)
              ? Math.max(0, this.serverAdjustedNow - timeResumed)
              : 0;
          return timeElapsedBefore + timeSinceResume;
        })
        .reduce((acc: number, cur: number) => acc + cur, 0);
    },
    borderColor() {
      if (this.numberCompleted >= this.numberOfTreatments) {
        return 'border--complete';
      }
      if (this.isOverTime) { return 'border--alert'; }
      if (this.isOverTimeAlmost) { return 'border--warn'; }
      return '';
    },
    timeTextColor(): string {
      return this.appointmentState === 'inactive'
        ? 'gray--text'
        : 'black--text';
    },
  },
  methods: {
    getTimeString(x: number /* milliseconds */) {

    },
    setTreatmentState(payload): void {
      const excludeCriterion = payload.state === 'completed' && (this.numberCompleted !== this.numberOfTreatments);
      if (excludeCriterion) { return; }
      this.treatmentState = payload.state;
    },
  },
});
</script>

<style scoped>
.timer-card {
  @apply items-center relative;
  width: 90%;
  height: 60px;
  padding: 2px 4px;
  background-color: #f5f6f7;
  display: grid;
  grid-template:
    "treatment-count  treatment-time  " auto
    "appointment-time appointment-time" 1fr
    / 1fr             auto;
  align-items: baseline;
  justify-items: center;
}

.treatment-count { justify-self: start }
.treatment-time {
  justify-self: end;
  letter-spacing: -0.5px !important;
}
.appointment-time {
  align-self: center;
  grid-area: appointment-time
}

.border--alert {
  border: 2px solid #e83123 !important;
}

.border--warn {
  border: 2px solid var(--v-yellow-base) !important;
}

.border--complete {
  border: 2px solid #a0fcd1 !important;
}

.unSelectedByDoctor {
  opacity: 0.3;
  cursor: default;
}
</style>
